import { FeatureFlag, FeatureFlags } from 'app/shared/featureFlags/models';

import { createAction } from 'app/shared/utils/action-helpers';
import { isEqual } from 'lodash';
import { selectAgent, selectIsUnit21User } from 'app/modules/session/selectors';
import { selectFeatureFlags } from 'app/shared/featureFlags/selectors';
import { setFeatureFlags } from 'app/shared/featureFlags/actions';
import { toSentenceCase } from 'app/shared/utils/string';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import styled from 'styled-components';

import { IconAlertTriangle, IconSearch } from '@u21/tabler-icons';
import {
  U21SideMenu,
  U21SideMenuProps,
} from 'app/shared/u21-ui/components/dashboard';
import {
  U21Button,
  U21Spacer,
  U21Switch,
  U21TextField,
  U21Typography,
} from 'app/shared/u21-ui/components';

export const SET_FEATURE_FLAG_ACTION_TYPE = '@@featureFlags/SET_FEATURE_FLAG';

// defined here to prevent other places using this devtool-only action
export const setFeatureFlag = (payload: {
  key: FeatureFlag;
  value: boolean | number | string;
}) => {
  return createAction(SET_FEATURE_FLAG_ACTION_TYPE, payload);
};

interface FeatureFlagOption {
  key: FeatureFlag;
  label: string;
  value: boolean | number | string;
}

const FE_FEATURE_FLAGS = new Set<string>(Object.values(FeatureFlag));

export const FeatureFlagControl = () => {
  const dispatch = useDispatch();
  const featureFlags = useSelector(selectFeatureFlags);
  const sessionAgent = useSelector(selectAgent);
  const isUnit21User = useSelector(selectIsUnit21User);

  const actualFlags = useFlags<FeatureFlags>();
  const ldClient = useLDClient();

  const [filter, setFilter] = useState('');

  const filteredFlags = useMemo(() => {
    const lowercaseFilter = filter.toLowerCase();
    return Object.entries(featureFlags)
      .map(([key, value]) => ({
        key,
        label: toSentenceCase(key.replaceAll('-', ' ')),
        value,
      }))
      .filter((i) => FE_FEATURE_FLAGS.has(i.key))
      .filter(
        (i) =>
          i.key.toLowerCase().includes(lowercaseFilter) ||
          i.label.toLowerCase().includes(lowercaseFilter),
      );
  }, [featureFlags, filter]);

  const ldConnected = ldClient?.getContext()?.email === sessionAgent.email;

  const flagsChanged = useMemo(
    () => ldConnected && !isEqual(actualFlags, featureFlags),
    [actualFlags, featureFlags, ldConnected],
  );

  // Check if we should show the toggle all button (exact search for "cm_delta_lake")
  const shouldShowToggleAll = filter.toLowerCase() === 'cm_delta_lake';

  // Determine the current state of filtered flags and what the toggle should do
  const toggleState = useMemo(() => {
    if (!shouldShowToggleAll) {
      return null;
    }

    // Only consider boolean flags for toggling
    const booleanFlags = filteredFlags.filter(
      (flag) => typeof flag.value === 'boolean',
    );

    const enabledCount = booleanFlags.filter((flag) => flag.value).length;
    const totalCount = booleanFlags.length;

    // If more than half are enabled, toggling will turn them all off
    // If half or less are enabled, toggling will turn them all on
    const shouldTurnOn = enabledCount <= totalCount / 2;

    return {
      shouldTurnOn,
      enabledCount,
      totalCount,
      booleanFlags,
    };
  }, [filteredFlags, shouldShowToggleAll]);

  const handleToggleAll = () => {
    if (!toggleState) return;

    const newValue = toggleState.shouldTurnOn;

    // Apply the toggle to all boolean flags in the filtered set
    toggleState.booleanFlags.forEach((flag) => {
      dispatch(
        setFeatureFlag({ key: flag.key as FeatureFlag, value: newValue }),
      );
    });
  };

  if (!isUnit21User) {
    return null;
  }

  const actionProps: Partial<U21SideMenuProps> | undefined = flagsChanged
    ? {
        actionButtonProps: { color: 'warning', children: 'Reset' },
        onAction: () => dispatch(setFeatureFlags(actualFlags)),
        footerLeftContent: (
          <U21Typography icon={<StyledIconAlertTriangle />} variant="body2">
            Flags Changed
          </U21Typography>
        ),
      }
    : undefined;

  return (
    <U21SideMenu title="Feature Flag Preview" {...actionProps}>
      <U21Spacer marginStart>
        <U21Typography variant="body2">
          Temporarily change feature flag values in your current browser tab
        </U21Typography>
        <U21Typography icon={<StyledIconAlertTriangle />} variant="subtitle2">
          Changes will reset if you refresh the page.
        </U21Typography>
        <U21TextField
          startIcon={<IconSearch />}
          onChange={(val?: string) => setFilter(val || '')}
          placeholder="Search feature flags"
          value={filter}
        />

        {shouldShowToggleAll && toggleState && (
          <ToggleAllContainer>
            <U21Button
              onClick={handleToggleAll}
              color={toggleState.shouldTurnOn ? 'primary' : 'info'}
              variant="outlined"
              size="small"
            >
              {toggleState.shouldTurnOn ? 'Enable All' : 'Disable All'} Filtered
              Flags
            </U21Button>
            <U21Typography variant="caption" color="text.secondary">
              {toggleState.enabledCount} of {toggleState.totalCount} flags
              enabled
            </U21Typography>
          </ToggleAllContainer>
        )}

        {filteredFlags.map((i: FeatureFlagOption) => {
          const { key, label, value } = i;
          return (
            <Container key={key}>
              <U21Typography variant="body2">{label}</U21Typography>
              {(() => {
                if (typeof value === 'boolean') {
                  return (
                    <U21Switch
                      checked={value}
                      onChange={(val) =>
                        dispatch(setFeatureFlag({ key, value: val }))
                      }
                    />
                  );
                }
                if (typeof value === 'number') {
                  return (
                    <StyledU21TextField
                      onChange={(val = 0) =>
                        dispatch(setFeatureFlag({ key, value: val }))
                      }
                      type="number"
                      value={value}
                    />
                  );
                }
                if (typeof value === 'string') {
                  return (
                    <StyledU21TextField
                      onChange={(val = '') =>
                        dispatch(setFeatureFlag({ key, value: val }))
                      }
                      value={value}
                    />
                  );
                }
                return null;
              })()}
            </Container>
          );
        })}
      </U21Spacer>
    </U21SideMenu>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ToggleAllContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 16px 0;
  padding: 12px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  background-color: ${(props) => props.theme.palette.background.paper};
`;

const StyledU21TextField = styled(U21TextField)`
  width: 100px;
`;

const StyledIconAlertTriangle = styled(IconAlertTriangle)`
  color: ${(props) => props.theme.palette.warning.main};
`;
